import { ArrowUpIcon, ChevronLeftIcon, HardHatIcon, MicIcon, PlusIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Phone hardware and the pieces of an SMS thread, drawn for the sign-in demo.
 *
 * `PhoneFrame` is the device: a precise SVG line drawing in the same flat `currentColor` language
 * as the skyline (hairline body, thin bezel, opaque screen) over a soft shadow. Anything passed as
 * `banner` renders inside the screen above the content, the way a notification slides in. The SMS
 * pieces (header, thread, bubbles with SVG tails, compose bar) are plain HTML mounted by the demo
 * timeline and animate in on mount. Purely decorative: render inside `aria-hidden`. Corners are
 * fixed on purpose; this is a picture of a phone, not app UI.
 */

export const PHONE_WIDTH = 256;
export const PHONE_HEIGHT = 520;
const BEZEL = 9;

/** Entrance for anything that appears on a screen. */
export const ENTER = 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:zoom-in-95 fill-mode-both duration-500';
/** Entrance for a notification banner sliding down from the top of the screen. */
const BANNER = 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-3 fill-mode-both duration-500';

export function PhoneFrame({ time, className, children, banner }: { time: string; className?: string; children: ReactNode; banner?: ReactNode }) {
  return (
    <div className={cn('text-foreground relative shrink-0 rounded-[38px] shadow-[0_28px_48px_-26px_rgba(0,0,0,0.4)] dark:shadow-[0_28px_48px_-22px_rgba(0,0,0,0.9)]', className)} style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }}>
      {/* Body, bezel and screen, with the side buttons 3px outside the body. */}
      <svg viewBox={`-3 0 ${PHONE_WIDTH + 6} ${PHONE_HEIGHT}`} width={PHONE_WIDTH + 6} height={PHONE_HEIGHT} className="absolute top-0 -left-[3px]" aria-hidden>
        <g className="fill-foreground/35">
          <rect x="-2.5" y="96" width="3" height="22" rx="1" />
          <rect x="-2.5" y="134" width="3" height="44" rx="1" />
          <rect x="-2.5" y="186" width="3" height="44" rx="1" />
          <rect x={PHONE_WIDTH - 0.5} y="150" width="3" height="68" rx="1" />
        </g>
        <rect x="0.75" y="0.75" width={PHONE_WIDTH - 1.5} height={PHONE_HEIGHT - 1.5} rx="38" className="fill-card" />
        <rect x="0.75" y="0.75" width={PHONE_WIDTH - 1.5} height={PHONE_HEIGHT - 1.5} rx="38" strokeWidth="1.25" className="fill-foreground/6 stroke-foreground/30" />
        <rect x="4.5" y="4.5" width={PHONE_WIDTH - 9} height={PHONE_HEIGHT - 9} rx="34.5" fill="none" className="stroke-foreground/8" />
        <rect x={BEZEL} y={BEZEL} width={PHONE_WIDTH - BEZEL * 2} height={PHONE_HEIGHT - BEZEL * 2} rx="30" className="fill-card stroke-foreground/12" />
      </svg>

      {/* Screen */}
      <div className="absolute flex flex-col overflow-hidden rounded-[30px]" style={{ inset: BEZEL }}>
        {/* Status bar: the island sits in the middle, the clock and the icons centred in the "ears" either side of it. */}
        <div className="flex h-[30px] shrink-0 items-center pt-1 text-[11px] font-semibold tabular-nums">
          <span className="flex flex-1 items-center justify-center pl-2">{time}</span>
          <span className="h-[18px] w-[60px] shrink-0 rounded-full bg-black ring-1 ring-white/10" aria-hidden />
          <span className="flex flex-1 items-center justify-center pr-2">
            <StatusIcons />
          </span>
        </div>
        {children}
        <div className="bg-foreground/30 mx-auto mt-0.5 mb-2 h-1 w-20 shrink-0 rounded-full" />
        {banner ? <div className={cn('absolute top-8 left-2 right-2 z-20', BANNER)}>{banner}</div> : null}
      </div>
    </div>
  );
}

/** iOS-style notification banner, for the moment a text lands. */
export function NotificationBanner({ title, body, when = 'now' }: { title: string; body: string; when?: string }) {
  return (
    <div className="bg-card/95 flex items-start gap-2.5 rounded-[16px] border px-3 py-2.5 shadow-[0_14px_30px_-12px_rgba(0,0,0,0.4)] backdrop-blur">
      <span className="bg-success/90 flex size-8 shrink-0 items-center justify-center rounded-[9px] text-white">
        <HardHatIcon className="size-4" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2 text-[9px]">
          <span className="truncate font-semibold tracking-wide uppercase">{title}</span>
          <span className="text-muted-foreground shrink-0">{when}</span>
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[10px] leading-snug">{body}</span>
      </span>
    </div>
  );
}

/** Blinking text caret for anything being typed. */
export function Caret() {
  return <span className="anim-caret ml-px inline-block h-[1em] w-px translate-y-[2px] bg-current align-baseline" aria-hidden />;
}

/** Signal bars, Wi-Fi and battery. */
function StatusIcons() {
  return (
    <svg viewBox="0 0 54 12" className="h-3 w-[54px] fill-current" aria-hidden>
      {/* Signal, Wi-Fi, battery: evenly spaced, each about 4px apart. */}
      <rect x="0" y="7" width="2.6" height="5" rx="0.6" />
      <rect x="4" y="5" width="2.6" height="7" rx="0.6" />
      <rect x="8" y="3" width="2.6" height="9" rx="0.6" />
      <rect x="12" y="1" width="2.6" height="11" rx="0.6" />
      <g transform="translate(-3.6 0)">
        <path d="M22.4 5.3a9.2 9.2 0 0 1 12.8 0l-1.7 1.7a6.8 6.8 0 0 0-9.4 0z" />
        <path d="M25.3 8.2a5.1 5.1 0 0 1 7 0l-1.7 1.7a2.7 2.7 0 0 0-3.6 0z" />
        <circle cx="28.8" cy="11" r="1.3" />
      </g>
      <rect x="35" y="1.5" width="17" height="9.5" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <rect x="36.5" y="3" width="12.5" height="6.5" rx="1.2" />
      <rect x="52.8" y="4.5" width="1.2" height="3.5" rx="0.5" opacity="0.5" />
    </svg>
  );
}

// ------------------------------------------------------------------------------------ SMS thread

/** Conversation header: back chevron, the job's avatar, saved name and number. */
export function SmsHeader({ name, number }: { name: string; number: string }) {
  return (
    <div className="border-foreground/10 relative flex shrink-0 flex-col items-center border-b px-3 pt-1 pb-2">
      <ChevronLeftIcon className="text-primary absolute top-2 left-2.5 size-4" strokeWidth={2.25} />
      <span className="bg-foreground/10 flex size-8 items-center justify-center rounded-full">
        <HardHatIcon className="size-4 opacity-70" strokeWidth={1.75} />
      </span>
      <span className="mt-1 text-[11px] leading-tight font-semibold">{name}</span>
      <span className="text-muted-foreground text-[9px] leading-tight tabular-nums">{number}</span>
    </div>
  );
}

/** The message list, bottom-anchored so new bubbles push older ones up. */
export function SmsThread({ dim = false, children }: { dim?: boolean; children: ReactNode }) {
  return <div className={cn('flex flex-1 flex-col justify-end gap-1.5 overflow-hidden px-3 pb-2 transition-opacity duration-500', dim && 'opacity-0')}>{children}</div>;
}

/** Compose bar; shows `draft` with a caret and a send button while the worker is typing. */
export function SmsComposeBar({ draft = '', placeholder = 'Text Message · SMS' }: { draft?: string; placeholder?: string }) {
  return (
    <div className="flex shrink-0 items-center gap-2 px-3 pt-1.5 pb-1">
      <span className="bg-foreground/10 flex size-6 shrink-0 items-center justify-center rounded-full">
        <PlusIcon className="size-3 opacity-70" strokeWidth={2.25} />
      </span>
      <span className={cn('border-foreground/15 flex h-7 flex-1 items-center justify-between rounded-full border pr-1 pl-3 text-[10px]', draft ? 'text-foreground font-medium tracking-wide' : 'text-muted-foreground')}>
        {draft ? (
          <>
            <span>
              {draft}
              <Caret />
            </span>
            <span className="bg-primary text-primary-foreground flex size-5 items-center justify-center rounded-full">
              <ArrowUpIcon className="size-3" strokeWidth={2.5} />
            </span>
          </>
        ) : (
          <>
            {placeholder}
            <MicIcon className="mr-1 size-3 opacity-70" />
          </>
        )}
      </span>
    </div>
  );
}

/** Centered timestamp between messages, the way messaging apps mark a gap. */
export function DayStamp({ children }: { children: ReactNode }) {
  return <p className={cn('text-muted-foreground py-1 text-center text-[9px] font-medium', ENTER)}>{children}</p>;
}

/** A message from the job number. Line breaks in the text are kept (body, blank line, opt-out footer). */
export function Incoming({ children }: { children: ReactNode }) {
  return (
    <div className={cn('flex justify-start pr-7', ENTER)}>
      <div className="bg-foreground/8 relative rounded-[16px] rounded-bl-none px-2.5 py-1.5 text-[11px] leading-[1.4] whitespace-pre-line">
        {children}
        <Tail side="in" />
      </div>
    </div>
  );
}

/** What the worker sent back, with an optional delivery receipt underneath. */
export function Outgoing({ receipt, children }: { receipt?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-end gap-0.5 pl-7">
      <div className={ENTER}>
        <div className="bg-primary text-primary-foreground relative rounded-[16px] rounded-br-none px-2.5 py-1.5 text-[11px] leading-[1.4] font-medium tracking-wide">
          {children}
          <Tail side="out" />
        </div>
      </div>
      {receipt ? <span className={cn('text-muted-foreground pr-1 text-[9px]', ENTER)}>{receipt}</span> : null}
    </div>
  );
}

/** The three bouncing dots while the job number is "typing" its automatic reply. */
export function TypingBubble() {
  return (
    <div className={cn('flex justify-start pr-7', ENTER)}>
      <div className="bg-foreground/8 relative flex h-[26px] items-center gap-[3px] rounded-[16px] rounded-bl-none px-2.5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="anim-typing-dot bg-foreground/45 size-1.5 rounded-full" style={{ animationDelay: `${i * 160}ms` }} />
        ))}
        <Tail side="in" />
      </div>
    </div>
  );
}

/** Bubble tail, flush against the square bottom corner on its side so the two shapes read as one. */
function Tail({ side }: { side: 'in' | 'out' }) {
  return (
    <svg viewBox="0 0 7 12" className={cn('absolute bottom-0 h-3 w-[7px]', side === 'in' ? 'fill-foreground/8 -left-[7px]' : 'fill-primary -right-[7px] -scale-x-100')} aria-hidden>
      <path d="M7 0v12C4.4 12 1.7 11.5 0 10.7 3.4 9.7 6.3 6.4 7 0Z" />
    </svg>
  );
}
